<template>
  <div
    class="shape-border-options"
  >
    <div
      v-if="showButtonStyleOptions"
      class="module-options__item module-options__item--button-style"
    >
      <legend class="module-options__legend">
        {{ $t('Style') }}
      </legend>
      <ButtonStyleSelect
        :canvas-name="canvasName"
        :module-data="moduleData"
        :aria-disabled="(!!moduleData.options.shape).toString()"
      />
    </div>
    <div class="module-options__item module-options__item--shape">
      <legend class="module-options__legend">
        {{ $t('Shape') }}
      </legend>
      <NebulaSelect>
        <template #select>
          <select
            class="nebula-select__element"
            :aria-disabled="(!!moduleData.options.button_style).toString()"
            :value="moduleData.options.shape"
            @change="updateShape"
          >
            <NebulaSelectOption
              :option="{
                text: $t('Rectangle (Default)'),
                value: '',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Circle'),
                value: 'circle',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Pentagon'),
                value: 'pentagon',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Rounded Rectangle'),
                value: 'rounded_rectangle',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Speech Bubble Left'),
                value: 'speech_bubble_lower_left',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Speech Bubble Right'),
                value: 'speech_bubble_lower_right',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Title Bubble'),
                value: 'title_bubble',
              }"
            />
          </select>
        </template>
      </NebulaSelect>
    </div>

    <div
      v-if="moduleData.options.shape && moduleData.options.shape !== 'default'"
      class="module-options__item module-options__item--border-type"
    >
      <legend class="module-options__legend">
        {{ $t('Border') }}
      </legend>
      <NebulaSelect>
        <template #select>
          <select
            class="nebula-select__element"
            :value="selectedBorderType"
            @change="updateBorderType"
          >
            <NebulaSelectOption
              :option="{
                text: $t('None'),
                value: '',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Solid'),
                value: 'solid',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Clouds'),
                value: 'clouds',
              }"
            />
          </select>
        </template>
      </NebulaSelect>
    </div>

    <div
      v-if="selectedBorderType && moduleData.options.shape
        && moduleData.options.shape !== 'default'"
      class="module-options__item module-options__item--border-variation"
    >
      <legend
        :id="`border-variation-combobox-label--${moduleData.id}`"
        class="module-options__legend"
      >
        {{ $t('Theme') }}
      </legend>
      <!-- eslint-disable max-len -->
      <NebulaCombobox
        ref="variationCombobox"
        :button-id="`border-variation-combobox-trigger--${uniqueId}`"
        @dropdownOpen="onVariationDropdownOpen"
      >
        <template
          #trigger="{
            toggleDropdown,
            checkArrowKeyOnTrigger,
            checkArrowKeyAfterTrigger,
            isOpen,
          }"
        >
          <button
            ref="variationTriggerButton"
            :id="`border-variation-combobox-trigger--${uniqueId}`"
            class="shape-border-options__variation-button"
            :aria-activedescendant="`border-variation-combobox-option--${moduleData.id}-${focusedBorderVariation}`"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :aria-labelledby="`border-variation-combobox-label--${moduleData.id}`"
            role="combobox"
            @click="toggleDropdown"
            @keydown="checkArrowKeyOnTrigger"
            @keyup="checkArrowKeyAfterTrigger"
          >
            <span class="shape-border-options__variation-button-text">
              {{ selectedBorderVariationLabel }}
            </span>
            <span class="nebula-select__open-indicator">
              <NebulaIcon
                class="nebula-select__open-icon"
                symbol-id="caret-down"
              />
            </span>
          </button>
        </template>
        <template #content>
          <NebulaListboxItem
            v-for="variation in variations"
            class="shape-border-options__variation-option"
            :class="`shape-border-options__variation-option--${currentPageTheme.name}-${variation.id}`"
            :text="variation.label"
            :key="variation.id"
            :id="`border-variation-combobox-option--${moduleData.id}-${variation.id}`"
            :selected="selectedBorderVariation === variation.id"
            @listboxItemClicked="updateBorderVariation(variation.id)"
            @listboxItemFocused="focusedBorderVariation = variation.id"
          />
        </template>
      </NebulaCombobox>
      <!-- eslint-enable max-len -->
    </div>
  </div>
</template>

<script>
import {
  NebulaCombobox,
  NebulaIcon,
  NebulaListboxItem,
  NebulaSelect,
  NebulaSelectOption,
} from '@discoveryedu/nebula-components';
import { cloneDeep, get, uniqueId } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { themes as themeConfigs } from '@/lib/constants/themes.json';
import ButtonStyleSelect from '@/components/ButtonStyleSelect.vue';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'ShapeBorderOptions',
  components: {
    ButtonStyleSelect,
    NebulaCombobox,
    NebulaIcon,
    NebulaListboxItem,
    NebulaSelect,
    NebulaSelectOption,
  },
  props: {
    canvasName: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      focusedBorderVariation: null,
      uniqueId: uniqueId(),
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    currentPageTheme() {
      return get(this, 'currentPage.options.theme', {
        name: 'default',
      });
    },
    draftIsMysteryTheme() {
      return this.currentPageTheme.name === 'mystery-science';
    },
    selectedBorderType() {
      return this.moduleData.options?.border?.type;
    },
    selectedBorderVariation() {
      const currentPageThemeConfig = themeConfigs
        .find((theme) => theme.id === (this.currentPageTheme.name || 'default'));

      const variation = this.moduleData.options?.border?.variation || 1;

      // If we've swapped to a different theme and the old variation index doesn't exist,
      // show the last index that does exist
      return Math.min(variation, currentPageThemeConfig.borderColors.length);
    },
    selectedBorderVariationLabel() {
      const variation = this.variations.find((v) => v.id === this.selectedBorderVariation);

      return variation && variation.label;
    },
    showButtonStyleOptions() {
      // Button style is only available for editorial users, when the current page has
      // the Mystery theme
      return this.draftIsMysteryTheme
        && this.isDeUser
        && (this.moduleData.type === 'button' || this.moduleData.type === 'advanced_button');
    },
    variations() {
      const currentPageThemeConfig = themeConfigs
        .find((theme) => theme.id === (this.currentPageTheme.name || 'default'));

      return currentPageThemeConfig.borderColors.map((variation) => ({
        ...variation,
        // Color names must be translated at runtime
        label: this.$t(variation.label) || variation.id.toString(),
      }));
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULE,
    ]),
    onVariationDropdownOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          // When the dropdown opens, check to see if it's overflowing the viewport
          const $dropdown = this.$refs.variationCombobox.$el.querySelector('.nebula-listbox__content');
          if ($dropdown) {
            const { height, y } = $dropdown.getBoundingClientRect();
            if (height + y > (window.innerHeight - 30)) {
              // If the dropdown is bleeding off the bottom of the viewport
              // or colliding with the bottom toolbar (30px tall), flip it
              // above the trigger button (with 4px margin)
              const { y: triggerButtonY } = this.$refs.variationTriggerButton
                .getBoundingClientRect();
              const dy = y + height - triggerButtonY + 4;

              // I don't want to set the transform directly but NebulaCombobox
              // doesn't have a prop for custom transform on its dropdown
              $dropdown.style.transform = `translateY(-${dy}px)`;
            }
          }
        });
      }
    },
    updateShape(e) {
      const data = cloneDeep(this.moduleData);
      data.options.shape = e.target.value || undefined;
      data.options.image_offset = undefined;

      // If we've added a shape mask and are editing a module that has
      // a layout, reset that layout to 'image' to prevent weird
      // masking
      if (data.options.shape && data.options.layout) {
        data.options.layout = 'image';
      }

      this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
    updateBorderType(e) {
      const data = cloneDeep(this.moduleData);

      // If we've set a value, save that. Otherwise clear the border property completely
      data.options.border = e.target.value
        ? {
          ...data.options.border,
          type: e.target.value,
        } : undefined;

      this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
    updateBorderVariation(variation) {
      const data = cloneDeep(this.moduleData);
      data.options.border = {
        ...data.options.border,
        variation,
      };
      this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
  },
};
</script>

<style lang="stylus">
.shape-border-options {
  border-top: 1px solid $nebula-color-platform-interface-300;
  display: flex;
  margin-top: $nebula-space-1x;
  padding-top: $nebula-space-1x;

  &__variation-button {
    // Match the styles of a NebulaSelect
    align-items: center;
    background: $nebula-color-white;
    border: 1px solid $nebula-color-interface-blue-300;
    border-radius: $nebula-border-radius-small;
    color: $nebula-color-neutral-500;
    display: flex;
    font-family: $nebula-font-family-display;
    font-size: $nebula-font-size-body-1;
    height: $nebula-space-5x;
    overflow: hidden;
    padding-block: 0;
    padding-inline: 12px 24px;
    position: relative;
    text-align: start;
    text-overflow: ellipsis;
    transition: border, box-shadow $nebula-transition-default;
    width: 100%;

    &:active, &:focus {
      border: 1px solid $nebula-text-color-link-hover;
      box-shadow: 0 0 0 3px rgba($nebula-text-color-link-default, 0.3);
      outline: 0;
    }

    .nebula-select__open-indicator {
      position: absolute;
      inset-inline-end: $nebula-space-1x;
      width: auto;
    }
  }

  &__variation-button-text {
    flex-grow: 1;
  }

  &__variation-option .nebula-listbox-item__text {
    &:before {
      background-color: black;
      border-radius: $nebula-border-radius-badge-default;
      box-shadow: 0 0 0 1px $nebula-color-platform-interface-600;
      content: '';
      display: inline-block;
      height: $nebula-space-2x;
      margin-bottom: -2px;
      margin-inline-end: $nebula-space-1x;
      width: $nebula-space-2x;
    }
  }

  .module-options__item--border-variation .nebula-listbox__content {
    z-index: 10; // prevent overlaps with other Nebula select boxes
  }
}
</style>
